<!--
  The playout timeline: planned and as-run on the same axis, so "what should have
  happened" and "what actually happened" are directly comparable (directive section 7).
-->
<script setup lang="ts">
import ShioPlayoutItem from './ShioPlayoutItem.vue';
import type { ContentType, PlayoutItemState } from '../onair.js';
import type { PlayoutItemLabels } from '../labels.js';

export interface TimelineItem {
  id: string;
  title: string;
  subtitle?: string | null;
  contentType: ContentType;
  state: PlayoutItemState;
  startsAt: string;
  durationMs?: number | null;
  isExactTime?: boolean;
  warning?: string | null;
}

withDefaults(
  defineProps<{
    items: TimelineItem[];
    /** When supplied, renders a second track of what actually aired. */
    asRun?: TimelineItem[];
    plannedLabel?: string;
    asRunLabel?: string;
    /** Passed through to every item, so the caller translates once. */
    itemLabels?: PlayoutItemLabels;
  }>(),
  { plannedLabel: 'Planned', asRunLabel: 'As run' },
);
</script>

<template>
  <div class="shio-timeline">
    <div class="shio-timeline__track">
      <p class="shio-label">{{ plannedLabel }}</p>
      <div role="list" :aria-label="plannedLabel">
        <div v-for="item in items" :key="item.id" role="listitem">
          <ShioPlayoutItem v-bind="item" :labels="itemLabels" />
        </div>
      </div>
    </div>

    <div v-if="asRun" class="shio-timeline__track">
      <p class="shio-label">{{ asRunLabel }}</p>
      <div role="list" :aria-label="asRunLabel">
        <div v-for="item in asRun" :key="item.id" role="listitem">
          <ShioPlayoutItem v-bind="item" :labels="itemLabels" />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.shio-timeline {
  display: grid;
  gap: var(--shio-space-4);
}

.shio-timeline:has(.shio-timeline__track + .shio-timeline__track) {
  grid-template-columns: 1fr 1fr;
}

.shio-timeline__track {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: var(--shio-space-2);
}

@media (max-width: 1023px) {
  .shio-timeline:has(.shio-timeline__track + .shio-timeline__track) {
    grid-template-columns: 1fr;
  }
}
</style>
